import React, { useEffect, useState } from 'react';
import { Input } from '@roger/ui-components';
import { Pencil } from 'lucide-react';
import { formatPhone } from '../../../utils/intake';

// Inline Editable Field Component (for header)
export const InlineEditableField: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}> = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder = 'N/A',
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);
    let formattedValue = e.target.value;

    // Apply formatting based on field label
    if (label === 'Phone' && formattedValue) {
      formattedValue = formatPhone(formattedValue);
    }

    onBlur(formattedValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setIsEditing(false);
      onBlur(value);
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <span className='inline-flex items-center'>
        <span className='text-muted-foreground'>{label}:</span>{' '}
        <Input
          ref={inputRef}
          value={value}
          onChange={e => onChange(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          inputSize='sm'
          className='w-32 inline-block ml-1'
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded px-1 -mx-1 transition-colors group ${
        disabled
          ? 'cursor-default'
          : 'cursor-pointer hover:bg-blue-50 hover:text-blue-700'
      }`}
      onClick={() => !disabled && setIsEditing(true)}
      title={disabled ? undefined : `Click to edit ${label.toLowerCase()}`}
    >
      <span
        className={`text-muted-foreground ${
          disabled ? '' : 'group-hover:text-blue-600'
        }`}
      >
        {label}:
      </span>{' '}
      <span className={disabled ? '' : 'group-hover:underline'}>
        {value || placeholder}
      </span>
      {!disabled && (
        <Pencil className='h-3 w-3 ml-1 hidden group-hover:block text-blue-500' />
      )}
    </span>
  );
};
